import { MAX_TRAINING_SESSIONS } from './trainingReportPolicy';

export const TRAINING_WEEKDAYS = [
  { value: 'lunes', label: 'Lunes', index: 1 },
  { value: 'martes', label: 'Martes', index: 2 },
  { value: 'miércoles', label: 'Miércoles', index: 3 },
  { value: 'jueves', label: 'Jueves', index: 4 },
  { value: 'viernes', label: 'Viernes', index: 5 },
  { value: 'sábado', label: 'Sábado', index: 6 },
  { value: 'domingo', label: 'Domingo', index: 0 },
];

const normalizeDate = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const normalizeTime = (value) => {
  if (!value) return '';
  return String(value).slice(0, 5);
};

export const parseStoredWeekdays = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim().toLowerCase()).filter(Boolean);
  }
  if (!value) return [];
  return String(value)
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
};

const dateFromIso = (value) => {
  const normalized = normalizeDate(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const [year, month, day] = normalized.split('-').map(Number);
  const result = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(result.getTime()) ? null : result;
};

const minutesFromTime = (value) => {
  const normalized = normalizeTime(value);
  if (!/^\d{2}:\d{2}$/.test(normalized)) return null;
  const [hours, minutes] = normalized.split(':').map(Number);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

export const calculateSchedule = (form) => {
  const start = dateFromIso(form.fecha_inicio);
  const end = dateFromIso(form.fecha_fin);
  const startMinutes = minutesFromTime(form.hora_inicio);
  const endMinutes = minutesFromTime(form.hora_fin);
  const selectedDays = new Set(form.dias || []);
  const allowedIndexes = new Set(
    TRAINING_WEEKDAYS
      .filter((day) => selectedDays.has(day.value))
      .map((day) => day.index),
  );

  if (
    !start ||
    !end ||
    end < start ||
    startMinutes === null ||
    endMinutes === null ||
    endMinutes <= startMinutes ||
    allowedIndexes.size === 0
  ) {
    return { sessionCount: 0, minutesPerSession: 0, totalMinutes: 0 };
  }

  let sessionCount = 0;
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    if (allowedIndexes.has(cursor.getUTCDay())) sessionCount += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  const minutesPerSession = endMinutes - startMinutes;
  return {
    sessionCount,
    minutesPerSession,
    totalMinutes: sessionCount * minutesPerSession,
  };
};

export const minutesToHoursText = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === '') return '';
  const numeric = Number(minutes);
  if (!Number.isFinite(numeric) || numeric <= 0) return '';
  const hours = numeric / 60;
  return Number.isInteger(hours) ? String(hours) : String(Number(hours.toFixed(2)));
};

export const hoursTextToMinutes = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) return null;
  return Math.round(hours * 60);
};

export const createGeneralDataForm = (report = {}) => {
  const form = {
    nombre_accion: report.nombre_accion || '',
    programa: report.programa || '',
    modalidad: report.modalidad || '',
    nombre_instructor: report.nombre_instructor || '',
    fecha_inicio: normalizeDate(report.fecha_inicio),
    fecha_fin: normalizeDate(report.fecha_fin),
    dias: parseStoredWeekdays(report.dias),
    hora_inicio: normalizeTime(report.hora_inicio),
    hora_fin: normalizeTime(report.hora_fin),
    duracion_horas: minutesToHoursText(report.duracion_programada_minutos),
    coordinador_nombre: report.coordinador_nombre || '',
    coordinador_cargo: report.coordinador_cargo || '',
  };

  const schedule = calculateSchedule(form);
  const storedDuration = Number(report.duracion_programada_minutos);
  if (
    schedule.totalMinutes > 0 &&
    Number.isFinite(storedDuration) &&
    storedDuration === schedule.totalMinutes
  ) {
    form.duracion_horas = '';
  }

  return form;
};

export const validateGeneralDataForm = (form) => {
  const errors = {};
  const requiredText = [
    ['nombre_accion', 'Captura el nombre de la acción de capacitación.'],
    ['programa', 'Captura el programa.'],
    ['modalidad', 'Captura la modalidad.'],
    ['nombre_instructor', 'Captura el nombre del instructor.'],
  ];

  requiredText.forEach(([field, message]) => {
    if (!String(form[field] || '').trim()) errors[field] = message;
  });

  if (!form.fecha_inicio) errors.fecha_inicio = 'Selecciona la fecha de inicio.';
  if (!form.fecha_fin) errors.fecha_fin = 'Selecciona la fecha de término.';
  if (!Array.isArray(form.dias) || form.dias.length === 0) {
    errors.dias = 'Selecciona al menos un día de capacitación.';
  }
  if (!form.hora_inicio) errors.hora_inicio = 'Captura la hora de inicio.';
  if (!form.hora_fin) errors.hora_fin = 'Captura la hora de término.';

  const schedule = calculateSchedule(form);
  if (
    form.fecha_inicio &&
    form.fecha_fin &&
    form.hora_inicio &&
    form.hora_fin &&
    form.dias?.length &&
    schedule.sessionCount === 0
  ) {
    errors.schedule =
      'Revisa el rango, los días y el horario. Debe existir al menos una sesión válida.';
  } else if (schedule.sessionCount > MAX_TRAINING_SESSIONS) {
    errors.schedule = `El reporte admite como máximo ${MAX_TRAINING_SESSIONS} sesiones. La configuración actual genera ${schedule.sessionCount}.`;
  }

  if (String(form.duracion_horas || '').trim()) {
    const minutes = hoursTextToMinutes(form.duracion_horas);
    if (!minutes) errors.duracion_horas = 'La duración debe ser mayor a cero.';
  }

  return errors;
};

export const buildGeneralDataPayload = (form) => ({
  nombre_accion: String(form.nombre_accion || '').trim(),
  programa: String(form.programa || '').trim(),
  modalidad: String(form.modalidad || '').trim(),
  nombre_instructor: String(form.nombre_instructor || '').trim(),
  fecha_inicio: form.fecha_inicio,
  fecha_fin: form.fecha_fin,
  dias: [...(form.dias || [])],
  hora_inicio: form.hora_inicio,
  hora_fin: form.hora_fin,
  duracion_programada_minutos: hoursTextToMinutes(form.duracion_horas),
  coordinador_nombre: String(form.coordinador_nombre || '').trim() || null,
  coordinador_cargo: String(form.coordinador_cargo || '').trim() || null,
});
